import createAuth0Client from '@auth0/auth0-spa-js'
import * as assert from 'assert-plus'
import Cookies from 'universal-cookie'
import jwtDecode from 'jwt-decode'
import React, { useEffect, useState } from 'react'

const DEFAULT_REDIRECT_CALLBACK = (appState = {}) => {
  if (!appState) return
  window.history.replaceState({}, document.title, window.location.pathname)
}
const DEFAULT_EMAILNOTVERIFIED_CALLBACK = (user = {}) => {
  if (!user) return
  throw new Error('Please verify your email before logging in.')
}

const DEFAULT_SSO_ENFORCED_CALLBACK = (domain, clientId) => () => {
  window.history.replaceState({}, document.title, window.location.pathname);
  sessionStorage.setItem('sso_enforced', 'true');
  window.location.href = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${encodeURIComponent(window.location.origin)}`;
}

const Auth0Context = React.createContext()
Auth0Context.displayName = 'Auth0Context'

const cookies = new Cookies();

// Key used to persist the invited user's email across the Auth0 redirect.
const EMAIL_HINT_KEY = 'login_email_hint'

/*
 * Resolve the email to use as the Auth0 `login_hint`.
 *
 * Reads `?email=` from the current URL and, when present, stashes it in
 * sessionStorage so the hint survives even if the query string is later
 * cleaned up (e.g. by the redirect callback). Falls back to the stashed
 * value when the URL no longer carries the param.
 */
const getEmailHint = () => {
  try {
    const email = new URLSearchParams(window.location.search).get('email')
    if (email) {
      sessionStorage.setItem(EMAIL_HINT_KEY, email)
      return email
    }
    return sessionStorage.getItem(EMAIL_HINT_KEY) || ''
  } catch (e) {
    return ''
  }
}

const clearEmailHint = () => {
  try {
    sessionStorage.removeItem(EMAIL_HINT_KEY)
  } catch (e) {
    // ignore
  }
}

// Routes whose unauthenticated redirect should open the Auth0 signup screen
// (screen_hint=signup) rather than the default login screen.
const SIGNUP_PATHS = ['/register']

const initiateLogin = (loginWithRedirect, params = {}) => {
  const url = typeof window !== 'undefined'
    ? window.location.pathname + window.location.search
    : ''
  const onSignupPath = typeof window !== 'undefined'
    && SIGNUP_PATHS.some(p => window.location.pathname.startsWith(p))
  return loginWithRedirect({
    appState: { targetUrl: url },
    login_hint: getEmailHint(),
    ...(onSignupPath ? { screen_hint: 'signup' } : {}),
    ...params,
  })
}

const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  onEmailNotVerifiedCallback = DEFAULT_EMAILNOTVERIFIED_CALLBACK,
  onSsoEnforcedCallback = DEFAULT_SSO_ENFORCED_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState()
  const [user, setUser] = useState()
  const [auth0Client, setAuth0] = useState()
  const [loading, setLoading] = useState(true)
  const [popupOpen, setPopupOpen] = useState(false)

  const ssoEnforcedCallback = onSsoEnforcedCallback === DEFAULT_SSO_ENFORCED_CALLBACK
    ? DEFAULT_SSO_ENFORCED_CALLBACK(initOptions.domain, initOptions.client_id)
    : onSsoEnforcedCallback;

  useEffect(() => {
    const updateAuthCookie = async () => {
      if (!isAuthenticated) {
        cookies.remove('MBEARERTOKEN')
      } else if (auth0Client) {
        const token = await auth0Client.getTokenSilently()
        const decoded = jwtDecode(token)
        if (decoded) {
          const expires_in = decoded.exp - decoded.iat
          cookies.set('MBEARERTOKEN', token, {
            path: '/',
            sameSite: 'strict',
            secure: true,
            maxAge: expires_in,
          });
        }
      }
    }
    updateAuthCookie()
  }, [isAuthenticated, user])

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions)
      setAuth0(auth0FromHook)

      if (sessionStorage.getItem('sso_enforced')) {
        console.log('sso_enforced flag found, redirecting to Google SSO');
        sessionStorage.removeItem('sso_enforced');
        await auth0FromHook.loginWithRedirect({ connection: 'google-oauth2', login_hint: getEmailHint() });
        return;
      }

      if (window.location.search.includes('error=access_denied') &&
        window.location.search.includes('error_description=SSO_ENFORCED')) {
        console.log('SSO enforced, calling ssoEnforcedCallback');
        ssoEnforcedCallback();
        return;
      }

      if (
        window.location.search.includes('code=') &&
        window.location.search.includes('state=')
      ) {
        const { appState } = await auth0FromHook.handleRedirectCallback()
        onRedirectCallback(appState)
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated()

      setIsAuthenticated(isAuthenticated)

      if (isAuthenticated) {
        // Login complete — drop the stashed hint so a later login in the same
        // tab doesn't reuse a stale email.
        clearEmailHint()
        const user = await auth0FromHook.getUser()
        if (!user.email_verified) {
          onEmailNotVerifiedCallback()
        }
        setUser(user)
      }

      setLoading(false)
    }
    initAuth0()
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true)
    try {
      await auth0Client.loginWithPopup(params)
    } catch (error) {
      console.error(error)
    } finally {
      setPopupOpen(false)
    }
    const user = await auth0Client.getUser()
    if (!user.email_verified) {
      onEmailNotVerifiedCallback()
    }
    setUser(user)
    setIsAuthenticated(true)
  }

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        loginWithPopup,
        getIdTokenClaims: (...p) => auth0Client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client.loginWithRedirect(...p),
        getTokenSilently: (...p) => auth0Client.getTokenSilently(...p),
        getTokenWithPopup: (...p) => auth0Client.getTokenWithPopup(...p),
        logout: (...p) => auth0Client.logout(...p)
      }}
    >
      {children}
    </Auth0Context.Provider>
  )
}

function Auth0Consumer({ children }) {
  return (
    <Auth0Context.Consumer>
      {context => {
        if (context === undefined) {
          throw new Error('Auth0Consumer must be used within a Auth0Provider')
        }
        return children(context)
      }}
    </Auth0Context.Consumer>
  )
}

const useAuth0 = () => {
  const context = React.useContext(Auth0Context)
  assert.object(context, 'useAuth0 must be used within a Auth0Provider')
  return context
}

export { Auth0Context, Auth0Consumer, Auth0Provider, useAuth0, initiateLogin }
